import { useEffect } from "react";
import { onMessage, MessagePayload } from "firebase/messaging";
import { getMessagingInstance } from "../services/firebase/firebaseClient";
import { handleIncomingPush } from "../services/firebase/handleIncomingPush";
import { isNativeWebView } from "../services/helpers/platform";

/**
 * Subscribes to foreground FCM messages once and delegates to the shared
 * handleIncomingPush handler — the same handler the native bridge uses, so
 * browser and app behave identically.
 *
 * Background/closed delivery is handled by public/firebase-messaging-sw.js
 * (browser) or native (Android/iOS). Inside a native WebView this hook is a
 * no-op (the native layer receives push and forwards it via the bridge).
 */
export function useFcmForeground() {
  useEffect(() => {
    if (isNativeWebView()) return;

    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      const messaging = await getMessagingInstance();
      if (!messaging || cancelled) return;

      unsubscribe = onMessage(messaging, (payload: MessagePayload) => {
        handleIncomingPush({
          notification: payload.notification,
          data: payload.data as Record<string, string> | undefined,
        });
      });
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);
}
